import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { TranslateModule } from '@ngx-translate/core';
import { ChildrenSelectorComponent, Student } from '../../shared/children-selector/children-selector.component';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

export interface Enseignant {
  id: number;
  nom: string;
  prenom: string;
  email: string;
}

export interface ApiSeance {
  id: number;
  nom: string;
  dateDebut: string;
  dateFin: string;
  classeId: number;
  classeNom: string;
  jitsiRoomName: string;
  jitsiLink: string;
  jitsiPassword?: string;
  statut: string; // 'EN_COURS', 'A_VENIR', 'TERMINEE', etc.
  dateCreation?: string;
  createdBy?: string;
  enseignants: Enseignant[];
  parentsCount?: number;
  idorg?: number;
  nameorg?: string;
}

@Component({
  selector: 'app-e-class',
  standalone: true,
  imports: [
    CommonModule, 
    RouterModule, 
    FormsModule, 
    TranslateModule, 
    ChildrenSelectorComponent
  ],
  templateUrl: './e-class.component.html',
  styleUrl: './e-class.component.css'
})
export class EClassComponent implements OnInit {
  selectedStudent: Student | null = null;
  seances: ApiSeance[] = [];
  isLoading = false;
  errorMessage = '';

  // Jitsi Live Modal State
  activeModalSeance: ApiSeance | null = null;
  safeJitsiUrl: SafeResourceUrl | null = null;
  showJitsiModal = false;

  // Fallback demo data from prompt in case API is unreachable
  private readonly fallbackSeances: ApiSeance[] = [
    {
      id: 2,
      nom: 'seance007',
      dateDebut: '2026-09-07T16:53',
      dateFin: '2026-09-07T21:52',
      classeId: 13449,
      classeNom: 'quatrième 2027',
      jitsiRoomName: 'educanet-seance-seance007-0f004e',
      jitsiLink: 'https://meet.jit.si/educanet-seance-seance007-0f004e',
      jitsiPassword: '689eef80d8',
      statut: 'EN_COURS',
      dateCreation: '2026-09-07T16:53:56.706',
      createdBy: 'manager',
      enseignants: [
        {
          id: 83,
          nom: 'GOUTA',
          prenom: 'Soumaya',
          email: 'soumaya.gouta@educanet.pro'
        }
      ],
      parentsCount: 1,
      idorg: 1,
      nameorg: 'magicland'
    }
  ];

  constructor(
    private http: HttpClient,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit() {
    // If no student is selected via selector, load default 125836
    this.fetchSeances(125836);
  }

  onStudentSelected(student: Student) {
    this.selectedStudent = student;
    const eleveId = student.idStudent || 125836;
    this.fetchSeances(eleveId);
  }

  fetchSeances(eleveId: number) {
    this.isLoading = true;
    this.errorMessage = '';
    const apiUrl = `http://54.37.245.19:2026/seance/api/seances/eleve/${eleveId}`;

    this.http.get<ApiSeance[]>(apiUrl).subscribe({
      next: (data) => {
        if (data && Array.isArray(data) && data.length > 0) {
          this.seances = data;
        } else {
          // Use fallback data if empty array returned from dev backend
          this.seances = this.fallbackSeances;
        }
        this.isLoading = false;
      },
      error: (err) => {
        console.warn('API call failed, using static fallback seances data:', err);
        this.seances = this.fallbackSeances;
        this.isLoading = false;
      }
    });
  }

  openJitsiModal(seance: ApiSeance) {
    this.activeModalSeance = seance;
    if (seance.jitsiLink) {
      // Sanitize URL for iframe embedding
      this.safeJitsiUrl = this.sanitizer.bypassSecurityTrustResourceUrl(seance.jitsiLink);
    }
    this.showJitsiModal = true;
  }

  closeJitsiModal() {
    this.showJitsiModal = false;
    this.activeModalSeance = null;
    this.safeJitsiUrl = null;
  }

  openExternalJitsi(url: string) {
    if (url) {
      window.open(url, '_blank');
    }
  }

  formatTeachers(teachers: Enseignant[]): string {
    if (!teachers || teachers.length === 0) return 'Non assigné';
    return teachers.map(t => `${t.prenom} ${t.nom}`).join(', ');
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  }

  formatTimeRange(startStr: string, endStr: string): string {
    if (!startStr) return '';
    const startFormatted = this.formatDate(startStr);
    if (!endStr) return startFormatted;
    try {
      const endD = new Date(endStr);
      const endHours = endD.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
      return `${startFormatted} - ${endHours}`;
    } catch {
      return `${startFormatted} - ${endStr}`;
    }
  }

  getStatusClass(statut: string): string {
    switch (statut?.toUpperCase()) {
      case 'EN_COURS':
        return 'status-en-cours';
      case 'TERMINEE':
      case 'ACHEVEE':
        return 'status-terminee';
      case 'A_VENIR':
      case 'EN_ATTENTE':
        return 'status-a-venir';
      default:
        return 'status-en-cours';
    }
  }

  getStatusLabel(statut: string): string {
    switch (statut?.toUpperCase()) {
      case 'EN_COURS':
        return 'En cours';
      case 'TERMINEE':
      case 'ACHEVEE':
        return 'Achevée';
      case 'A_VENIR':
      case 'EN_ATTENTE':
        return 'A venir';
      default:
        return statut || 'En cours';
    }
  }
}
